import * as React from "react";
import {
  Card,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Avatar,
  Chip,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import IconButton from "@mui/material/IconButton";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Stack } from "@mui/system";

const SERIES = [
  { id: "qftd_ftd", label: "QFTD/FTD", dataKey: "qftd_ftd", color: "#F59E0B" },
  { id: "ftd_regs", label: "FTD/Regs", dataKey: "ftd_regs", color: "#3B82F6" },
  { id: "out_in", label: "Out/In", dataKey: "out_in", color: "#8B5CF6" },
  { id: "regs_out", label: "Regs/Out", dataKey: "regs_out", color: "#F87171" },
];

const chartData = [
  { x: "08 Oct", qftd_ftd: 118, ftd_regs: 102, out_in: 60, regs_out: 32 },
  { x: "11 Oct", qftd_ftd: 119, ftd_regs: 100, out_in: 63, regs_out: 44 },
  { x: "14 Oct", qftd_ftd: 116, ftd_regs: 92, out_in: 68, regs_out: 27 },
  { x: "17 Oct", qftd_ftd: 117, ftd_regs: 96, out_in: 65, regs_out: 35 },
  { x: "20 Oct", qftd_ftd: 114, ftd_regs: 85, out_in: 73, regs_out: 32 },
  { x: "23 Oct", qftd_ftd: 116, ftd_regs: 93, out_in: 67, regs_out: 38 },
  { x: "26 Oct", qftd_ftd: 118, ftd_regs: 100, out_in: 62, regs_out: 40 },
  { x: "29 Oct", qftd_ftd: 115, ftd_regs: 82, out_in: 75, regs_out: 30 },
  { x: "01 Nov", qftd_ftd: 120, ftd_regs: 104, out_in: 60, regs_out: 41 },
  { x: "04 Nov", qftd_ftd: 116, ftd_regs: 89, out_in: 72, regs_out: 42 },
];

const notes = [2, 4, 8];

// Custom dot — hollow normally, filled + enlarged when active
const CustomDot = (props: any) => {
  const { cx, cy, activeIndex, index, color } = props;
  if (cx === undefined || cy === undefined) return null;
  const isActive = activeIndex === index;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isActive ? 7 : 4}
      fill={isActive ? color : "white"}
      stroke={isActive ? "white" : color}
      strokeWidth={isActive ? 2.5 : 1.5}
      style={
        isActive
          ? { filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.25))" }
          : undefined
      }
    />
  );
};

export default function FunnelRatioMultiChart() {
  const [range, setRange] = React.useState("day");
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  const [activeNote, setActiveNote] = React.useState<number | null>(null);
  const [showFullNote, setShowFullNote] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const resize = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    resize.observe(chartRef.current);
    return () => resize.disconnect();
  }, []);

  const handleChartClick = (rechartsData: any) => {
    if (!rechartsData || rechartsData.activeTooltipIndex === undefined) {
      setActiveIndex(null);
      return;
    }
    const index = rechartsData.activeTooltipIndex;
    // Toggle off if same point clicked again
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const handleNoteClick = (index: number) => {
    setActiveNote((prev) => (prev === index ? null : index));
  };

  const getNoteLeft = (index: number) => {
    const marginLeft = 40;
    const marginRight = 24;
    const usableWidth = chartWidth - marginLeft - marginRight;
    const step = usableWidth / (chartData.length - 1);
    return marginLeft + step * index;
  };

  return (
    <Card sx={{ borderRadius: spacing.Radius.md.$value, boxShadow: 3, mb: 1 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ padding: "16px 16px 0px 16px" }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={typography.Font_weight.Medium.$value}
          color={colors.GrayLightMode[700].$value}
        >
          Funnel Ratio Over Time
        </Typography>

        <FormControl size="small">
          <Select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            sx={{
              borderRadius: 2,
              height: 32,
              minWidth: 85,
              padding: "6px 0px",
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              color: colors.GrayLightMode[700].$value,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.GrayLightMode[300].$value,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.Brand[600].$value,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.Brand[600].$value,
              },
              "& .MuiSelect-icon": {
                color: colors.GrayLightMode[700].$value,
              },
            }}
          >
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Chart */}
      <Box ref={chartRef} sx={{ width: "100%", position: "relative" }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 0, bottom: 32 }}
            onClick={handleChartClick}
            style={{ cursor: "pointer" }}
          >
            <Legend
              verticalAlign="top"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{
                fontSize: typography.Size.Text.xxs.$value,
                paddingLeft: 10,
                paddingBottom: 16,
              }}
              formatter={(value) => (
                <span style={{ color: colors.GrayLightMode[600].$value }}>
                  {value}
                </span>
              )}
            />
            <CartesianGrid
              strokeDasharray=""
              vertical={false}
              stroke={colors.GrayLightMode[300].$value}
              opacity={0.3}
            />
            <XAxis
              dataKey="x"
              tick={(props: any) => {
                const { x, y, payload } = props;
                const [day, month] = payload.value.split(" ");
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    fill={colors.GrayLightMode[600].$value}
                    fontSize={typography.Size.Text.xxs.$value}
                    fontWeight={typography.Font_weight.Medium.$value}
                  >
                    <tspan x={x} dy="0.5em">
                      {day}
                    </tspan>
                    <tspan x={x} dy="1.2em">
                      {month}
                    </tspan>
                  </text>
                );
              }}
              tickLine={false}
              axisLine={false}
              interval={0}
            />
            <YAxis
              domain={[20, 120]}
              tickCount={6}
              tick={{
                fill: colors.GrayLightMode[600].$value,
                fontSize: typography.Size.Text.xxs.$value,
                fontWeight: typography.Font_weight.Medium.$value,
              }}
              tickLine={false}
              axisLine={false}
              width={40}
            />

            {/* ✅ Built-in Recharts tooltip */}
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                fontSize: typography.Size.Text.xs.$value,
                padding: "8px",
                display: "flex",
              }}
              labelStyle={{
                color: colors.GrayLightMode[500].$value,
                fontWeight: 500,
                marginBottom: 4,
              }}
              itemStyle={{
                padding: "2px 0",
              }}
              cursor={{
                stroke: colors.GrayLightMode[200].$value,
                strokeWidth: 1,
              }}
              labelFormatter={() => ""} // ✅ hides the date
            />
            {SERIES.map((s) => (
              <Line
                key={s.id}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.5}
                dot={(dotProps: any) => (
                  <CustomDot
                    {...dotProps}
                    activeIndex={activeIndex}
                    color={s.color}
                  />
                )}
                activeDot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Note Icons */}
        {notes.map((index) => (
          <IconButton
            key={index}
            size="small"
            sx={{
              position: "absolute",
              bottom: 4,
              left: getNoteLeft(index),
              transform: "translateX(-50%)",
            }}
            onClick={() => handleNoteClick(index)}
          >
            <StickyNote2OutlinedIcon sx={{ width: "14px" }} />
          </IconButton>
        ))}
      </Box>

      {/* Collapse note panel */}
      <Collapse in={activeNote !== null}>
        <Box
          sx={{
            display: "flex",
            px: 1.5,
            py: 1,
            gap: 0.5,
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Stack flexDirection="row" gap={0.5} alignItems="center">
            <Avatar sx={{ width: 18, height: 18 }} src="#" />
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              lineHeight={typography.Line_height.text.xs.$value}
              fontWeight={typography.Font_weight.Regular.$value}
              color={colors.GrayLightMode[700].$value}
            >
              Netanel Dahan
            </Typography>
            <Chip
              label="Product & Services"
              variant="outlined"
              size="small"
              sx={{
                height: spacing.Spacing["1xl"].$value,
                fontSize: typography.Size.Text.xxs.$value,
                lineHeight: typography.Line_height.text.xxs.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                color: colors.Brand[700].$value,
                borderColor: colors.Brand[700].$value,
                borderRadius: spacing.Radius["2xl"].$value,
                ml: 0.5,
              }}
            />
          </Stack>

          <Box>
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              color={colors.GrayLightMode[900].$value}
              fontWeight={typography.Font_weight.Semibold.$value}
            >
              Bing - Bingo main 2 tcpa & bingo main 2[bingo] tier3 tcpa & bingo
              uk bonus tcp - Into one ad group
            </Typography>
            <Collapse in={showFullNote}>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                color={colors.GrayLightMode[900].$value}
                fontWeight={typography.Font_weight.Semibold.$value}
                mt={0.5}
              >
                This is the complete note text. Here you can show the full
                description of the note including campaign details, targeting,
                ad group adjustments, or any additional commentary related to
                this chart point.
              </Typography>
            </Collapse>
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              fontWeight={typography.Font_weight.Medium.$value}
              color={colors.Brand[700].$value}
              mt={0.5}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setShowFullNote((prev) => !prev)}
            >
              {showFullNote ? "Hide full Note" : "See full Note"}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
}
